import React, { createContext, useState, useCallback, useContext } from 'react';
import './ToastContext.css';
import { View } from '../App';

interface ToastProps {
    message: string;
    type: 'success' | 'error' | 'info';
    onDismiss: () => void;
    onClick?: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onDismiss, onClick }) => (
    <div className={`toast toast-${type} ${onClick ? 'clickable' : ''}`} onClick={() => {
        onDismiss();
        onClick?.();
    }}>
        {message}
    </div>
);

interface ToastMessage {
    id: number;
    message: string;
    type: 'success' | 'error' | 'info';
    onClick?: () => void;
}

interface ToastContextType {
    addToast: (message: string, type: 'success' | 'error' | 'info', onClick?: () => void) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    const addToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info', onClick?: () => void) => {
        const id = Date.now() + Math.random();
        setToasts(prevToasts => [...prevToasts, { id, message, type, onClick }]);
        setTimeout(() => {
            setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
        }, 5000);
    }, []);

    const dismissToast = (id: number) => {
        setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
    };

    return (
        <ToastContext.Provider value={{ addToast }}>
            {children}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onDismiss={() => dismissToast(toast.id)}
                        onClick={toast.onClick}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};